import axios from 'axios';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actionTypes } from '../../constants/actionTypes';
import { trackAnalyticsEvent } from '../../constants/analyticsMeta';
import {
  DAY_PASS_LITE_PLAN_ID,
  LITE_USD_MONTHLY,
  LITE_USD_YEARLY,
  PRO_USD_MONTHLY,
  PRO_USD_YEARLY,
  STARTER_USD_MONTHLY,
  STARTER_USD_YEARLY,
  ULTRA_USD_MONTHLY,
  ULTRA_USD_YEARLY,
  USER_CLASSIFICATION,
} from '../../utility/common';
import { signOut } from '../../utility/services/authActions';
import { useSession } from '../../utility/services/customHooks';
import { ISPDomains, SUBSCRIPTION_STATUS } from '../../constants/common';
import { setCookie } from '../content-ai-tools/components/tools-common';

const CreditsComponent = () => {
  const [timeLeft, setTimeLeft] = useState('');
  const [timeLeftForDayPass, setTimeLeftForDayPass] = useState('');
  const [remainingDayPassUsage, setRemainingDayPassUsage] = useState(0);
  const session = useSession();
  const router = useRouter();
  let dayPassCookie = Cookies.get('qttr_tools_daypass_auth');
  const [dayPassAuth, setDayPassAuth] = useState(dayPassCookie || '');
  const dispatch = useDispatch();
  const creditsRemaining = useSelector((state) => state.root.creditsRemaining);
  const usagePerDay = useSelector((state) => state.root.creditsUsagePerDay);
  const dayPassCreditsRemaining = useSelector((state) => state.root.dayPassCreditsRemaining || 0);

  const decodeJwt = (token) => {
    try {
      if (token) {
        const [headerEncoded, payloadEncoded, signatureEncoded] = token.split('.');
        // Decode the base64-encoded payload
        const payload = JSON.parse(Buffer.from(payloadEncoded, 'base64').toString('utf-8'));
        return payload;
      }
    } catch (error) {
      console.error('decodeJwt=>', error);
    }
  };

  const getDayPassUsage = async (email = '', firstName = '', lastName = '') => {
    try {
      const qttr_tools_daypass_auth_token = Cookies.get('qttr_tools_daypass_auth') || '';
      const dayPassTokenDetails = qttr_tools_daypass_auth_token ? decodeJwt(qttr_tools_daypass_auth_token) : {};
      const isDayPassLite = DAY_PASS_LITE_PLAN_ID === dayPassTokenDetails?.plan_id;

      // Reference to track if refresh is already triggered
      // const hasRefreshed = useRef(false);

      if (isDayPassLite) {
        const payloadBody = {
          email: email,
          firstName: firstName,
          lastName: lastName,
        };

        // Decode the base64-encoded payload
        const res = await axios.post('/api/join-content-ai/get-user-day-pass-usage', payloadBody, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (res?.data?.data?.remaining !== undefined) {
          dispatch({
            type: actionTypes.SET_REMAINING_DAY_PASS_CREDITS,
            dayPassCreditsRemaining: res?.data?.data?.remaining || 0,
          });
          if (res?.data?.data?.remaining === 0 && dayPassAuth) {
            dayPassCookie = '';
            setDayPassAuth('');
            setCookie('qttr_tools_daypass_auth', '', false, true);
            if (
              ![SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED].includes(
                session?.user?.qwa_tools_subscription_status || ''
              ) &&
              creditsRemaining === 0
            ) {
              getPaidToolPlansCreditsRemaining(session?.user?.email, session?.user?.userType, session?.user?.planId);
            }
          }
        }
      }
    } catch (error) {
      console.error('handleGetCheckoutPage', error);
    }
  };

  const [planId, setPlanId] = React.useState('');
  const [showFreeCredits, setShowFreeCredits] = useState(false);

  const checkIfUserHasPurchasedDayPass = async () => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      let payloadBody = {
        email: session?.session?.user?.email,
        firstName: session?.session?.user?.name?.split(' ')[0] || '',
        lastName: session?.session?.user.name?.split(' ')[1] || '',
        timezone,
        isQwaDashboard: true,
      };

      const res = await axios.post('/api/join-content-ai/verify-day-pass-purchased', payloadBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (res?.data?.data && res?.data?.data?.id) {
        const dayPassCookie = Cookies.get('qttr_tools_daypass_auth');
        setDayPassAuth(dayPassCookie);
        getDayPassUsage(
          session?.session?.user?.email,
          session?.session?.user?.name?.split(' ')?.[0],
          session?.session?.user.name?.split(' ')?.[1]
        );
      }
    } catch (error) {
      console.error('checkIfUserHasPurchasedDayPass::', error);
    }
  };

  useEffect(() => {
    if (router?.query?.daypass === 'success') {
      checkIfUserHasPurchasedDayPass();
    } else if (session?.session?.user?.email || ['ai-tools-home', 'add-credits'].includes(router?.pathname)) {
      checkIfUserHasPurchasedDayPass();
    }
  }, [session, router]);

  useEffect(() => {
    let plan = '';
    if (session?.session?.user) {
      plan = session?.session?.user?.planId;
    }

    if (plan == STARTER_USD_MONTHLY) {
      setPlanId('Starter Plan User');
    } else if (plan == LITE_USD_MONTHLY) {
      setPlanId('Lite Plan User');
    } else if (plan == PRO_USD_MONTHLY) {
      setPlanId('Pro Plan User');
    } else if (plan == ULTRA_USD_MONTHLY) {
      setPlanId('Ultra Plan User');
    } else if (plan == STARTER_USD_YEARLY) {
      setPlanId('Starter Plan Yearly User');
    } else if (plan == LITE_USD_YEARLY) {
      setPlanId('Lite Plan Yearly User');
    } else if (plan == PRO_USD_YEARLY) {
      setPlanId('Pro Plan Yearly User');
    } else if (plan == ULTRA_USD_YEARLY) {
      setPlanId('Ultra Plan Yearly User');
    }

    setDayPassAuth(dayPassCookie);

    if (dayPassCookie) {
      const payload = decodeJwt(dayPassCookie);
      if (payload?.exp) {
        const timestamp = payload.exp;

        // Get current date and time in user's local timezone
        const now = new Date();

        // Calculate remaining time in seconds
        const currentTimeInSeconds = Math.floor(now.getTime() / 1000);
        const remainingSeconds = timestamp - currentTimeInSeconds;

        // Function to format remaining time in HH:mm format
        const formatTime = (seconds) => {
          const hours = Math.floor(seconds / 3600);
          const minutes = Math.floor((seconds % 3600) / 60);
          return `${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m`;
        };

        // Function to update and display remaining time
        const updateRemainingTime = () => {
          const currentTimeInSeconds = Math.floor(Date.now() / 1000);
          const remainingSeconds = timestamp - currentTimeInSeconds;

          if (remainingSeconds <= 0) {
            clearInterval(intervalId);
            console.log('Time expired!');
            return;
          }

          const formattedTime = formatTime(remainingSeconds);

          setTimeLeftForDayPass(formattedTime); // Set the formatted time in your state or wherever needed
        };

        // Update remaining time immediately and then every second
        updateRemainingTime();
        const intervalId = setInterval(updateRemainingTime, 1000);
      }
    }
  }, [session, dayPassAuth]);

  useEffect(() => {
    const callFunctionAtMidnight = () => {
      console.log('Function called at 12 AM local time');
    };

    const calculateTimeUntilMidnight = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0); // Set time to midnight

      return midnight - now;
    };

    const formatTimeLeft = (milliseconds) => {
      const totalSeconds = Math.floor(milliseconds / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    };

    const scheduleMidnightFunction = () => {
      const timeUntilMidnight = calculateTimeUntilMidnight();
      setTimeLeft(formatTimeLeft(timeUntilMidnight));

      const countdownInterval = setInterval(() => {
        const newTimeUntilMidnight = calculateTimeUntilMidnight();
        setTimeLeft(formatTimeLeft(newTimeUntilMidnight));

        if (newTimeUntilMidnight <= 0) {
          clearInterval(countdownInterval);
          callFunctionAtMidnight();

          // Schedule the function to be called every 24 hours thereafter
          setInterval(callFunctionAtMidnight, 24 * 60 * 60 * 1000);
        }
      }, 1000);
    };

    scheduleMidnightFunction();
  }, []);

  const getPaidToolPlansCreditsRemaining = async (email, userType, planId) => {
    try {
      const payloadBody = {
        email,
        userType,
        planId,
      };
      const result = await axios.post('/api/join-content-ai/get-paid-tools-plan-usage', payloadBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (result?.data?.data) {
        dispatch({
          type: actionTypes.SET_CREDITS_REMAINING,
          creditsRemaining: result?.data?.data?.remaining,
        });
        dispatch({
          type: actionTypes.SET_CREDITS_REQUIRED_PER_DAY,
          creditsUsagePerDay: result?.data?.data?.usageLimitPerDay,
        });
      }
    } catch (error) {
      console.error('getCreditsRemaining :: credit meter box :: Error ::', error);
      if (
        [403, 401].includes(error?.response?.status) &&
        ['Requires Authentication', 'Access Denied!!!'].includes(error?.response?.data)
      ) {
        await signOut({ callbackUrl: '/login', redirect: true });
        trackAnalyticsEvent({
          eventName: 'Logged out due to session expiry',
          payload: {
            label: 'Logged out due to session expiry',
            methodCalled: 'getCreditsRemaining::creditBox',
          },
        });
      }
    }
  };

  const isCorporateAndValidEmail = (e) => {
    const str = e.toLowerCase();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const allowedPersonalEmails = new Set([
      'maheshauti00@gmail.com',
      'sanjoli1097@gmail.com',
      'bmmc1990@gmail.com',
      // Add more allowed emails here
    ]);

    // Check for ISP domains
    const emailDomain = str.split('@')[1]; // Extract domain from email
    if (ISPDomains.includes(emailDomain)) {
      return false; // ISP domain found
    }

    const nonCorporateDomains = [
      'gmail.com',
      'outlook.com',
      'hotmail.com',
      'yahoo.com',
      'me.com',
      'icloud.com',
      'msn.com',
      'live.com',
      'proton.me',
      '.edu',
      'ac.in',
    ];

    if (!emailPattern.test(str)) return false;
    if (allowedPersonalEmails.has(str)) return true;

    return !nonCorporateDomains.some((domain) => {
      if (domain === '.edu') {
        return str.endsWith('.edu') || str.endsWith('.edu.');
      }
      return str.endsWith(domain);
    });
  };

  const getFreeToolsRemainingUsage = async () => {
    try {
      let qttr_tools_daypass_auth = Cookies.get('qttr_tools_daypass_auth');
      if (
        (!qttr_tools_daypass_auth &&
          session &&
          (!session?.session?.user?.qwa_tools_subscription_status ||
            ['subscription_activated', 'subscription_created', 'subscription_cancelled'].includes(
              session?.session?.user?.qwa_tools_subscription_status
            ))) ||
        session?.session?.userType === 'QWA_TOOLS'
      ) {
        const payloadBody = {
          email: session?.session?.user?.email,
        };

        const res = await axios.post('/api/join-content-ai/get-free-tools-usage', payloadBody, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        setShowFreeCredits(true);
        if (res?.data?.data?.remaining !== undefined) {
          dispatch({
            type: actionTypes.SET_CREDITS_REMAINING,
            creditsRemaining: res?.data?.data?.remaining,
          });
          let isCorp = false;
          if (payloadBody?.email) {
            isCorp = isCorporateAndValidEmail(payloadBody?.email);
          }
          dispatch({
            type: actionTypes.SET_CREDITS_REQUIRED_PER_DAY,
            creditsUsagePerDay: isCorp ? 10 : 7,
          });
        }
      } else {
        console.log('user has not verified its email or its dayPass is active');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (
      session?.session?.user?.email &&
      session?.session?.user?.planId &&
      !['subscription_cancelled'].includes(session?.session?.user?.qwa_tools_subscription_status) &&
      !dayPassAuth
    ) {
      getPaidToolPlansCreditsRemaining(
        session.session.user.email,
        session.session.user.userType,
        session.session.user.planId
      );
    } else if (
      session &&
      (!session?.session?.user?.planId || session?.session?.user?.planId) &&
      ['QWA_TOOLS', 'CONTENT_AI'].includes(session?.session?.user?.userType) &&
      !dayPassAuth
    ) {
      getFreeToolsRemainingUsage();
    }
  }, [dayPassAuth, getFreeToolsRemainingUsage, getPaidToolPlansCreditsRemaining, session]);

  return (
    <div className="credits-container-main">
      <div className="available-credits-section">
        <p className="credits-header m-0">Available Credits</p>
        {decodeJwt(dayPassAuth) && dayPassCreditsRemaining ? (
          <div className="day-pass-active-text">DAY PASS - ACTIVE</div>
        ) : null}
      </div>

      <div className="credits-details">
        <div className="credits">
          <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Dashboard" clip-path="url(#clip0_2554_1780)">
              <path
                id="Vector (Stroke)"
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M3.41138 1.2522C3.67479 0.988799 4.03204 0.84082 4.40455 0.84082H17.1158C17.488 0.841148 17.8456 0.989219 18.1087 1.25249L21.5425 4.68634C21.6733 4.81691 21.777 4.972 21.8477 5.14273C21.9184 5.31346 21.9547 5.49645 21.9545 5.68123V12.4999C21.9545 12.9141 21.6188 13.2499 21.2045 13.2499C20.7903 13.2499 20.4545 12.9141 20.4545 12.4999V5.71966L20.4822 5.74733C20.4735 5.73853 20.4666 5.72811 20.4618 5.71665C20.4571 5.70522 20.4546 5.69299 20.4545 5.68064V5.71966L17.0757 2.34082H17.1158C17.0907 2.3408 17.0659 2.33085 17.048 2.31315M17.048 2.31315L17.0757 2.34082H4.5V2.24537C4.5 2.27068 4.48994 2.29496 4.47204 2.31286C4.45414 2.33076 4.42986 2.34082 4.40455 2.34082H4.5V22.659H4.40455C4.42986 22.659 4.45414 22.6691 4.47204 22.687C4.48994 22.7049 4.5 22.7291 4.5 22.7545V22.659H11.3864C11.8006 22.659 12.1364 22.9948 12.1364 23.409C12.1364 23.8232 11.8006 24.159 11.3864 24.159H4.40455C4.03204 24.159 3.67479 24.011 3.41138 23.7476C3.14798 23.4842 3 23.127 3 22.7545V2.24537C3 1.87286 3.14798 1.51561 3.41138 1.2522"
                fill="#FFC72C"
              />
              <path
                id="Vector"
                d="M10.1592 14.9L12.6592 13.5L11.2592 16L12.6592 18.5L10.1592 17.1L7.65918 18.5L9.05918 16L7.65918 13.5L10.1592 14.9Z"
                fill="#FFC72C"
              />
              <path
                id="Vector_2"
                d="M15.7883 17.6129L14.2638 16.0884L15.5884 14.7638L17.1129 16.2883L15.7883 17.6129ZM15.1448 14.1828L13.6828 15.6448C13.4391 15.876 13.4391 16.2821 13.6828 16.5258L20.9742 23.8172C21.2179 24.0609 21.624 24.0609 21.8552 23.8172L23.3172 22.3552C23.5609 22.124 23.5609 21.7179 23.3172 21.4742L16.0258 14.1828C15.7821 13.9391 15.376 13.9391 15.1448 14.1828Z"
                fill="#FFC72C"
              />
              <path
                id="Vector_3"
                d="M9.15016 8.22635L5.99998 8.70057L8.43809 6.65017L7.96387 3.5L10.0143 5.9381L13.1644 5.46388L10.7263 7.51428L11.2006 10.6645L9.15016 8.22635Z"
                fill="#FFC72C"
              />
              <path
                id="Vector_4"
                d="M15.67 11.5059L13 11.9079L15.0665 10.17L14.6646 7.5L16.4024 9.56647L19.0724 9.16453L17.0059 10.9024L17.4079 13.5724L15.67 11.5059Z"
                fill="#FFC72C"
              />
            </g>
            <defs>
              <clipPath id="clip0_2554_1780">
                <rect width="24" height="24" fill="white" transform="translate(0 0.5)" />
              </clipPath>
            </defs>
          </svg>
          {decodeJwt(dayPassAuth) && dayPassCreditsRemaining ? (
            <div className="scores-container">
              <p className="remaining-score-credits m-0">
                {dayPassCreditsRemaining < 10 ? `0${dayPassCreditsRemaining}` : `${dayPassCreditsRemaining}`}/20
              </p>
            </div>
          ) : (
            <div className="scores-container">
              <p className="remaining-score-credits m-0">
                {creditsRemaining >= 0 ? (creditsRemaining >= 10 ? creditsRemaining : `0${creditsRemaining}`) : 0}
              </p>
              <p className="out-of-score-credits m-0">/{usagePerDay >= 10 ? usagePerDay : `0${usagePerDay}`}</p>
            </div>
          )}

          <p
            className="add-credits m-0"
            onClick={() => {
              router.push('/app/add-credits');
              trackAnalyticsEvent({
                eventFor: 'button',
                eventName: 'Clicked on Add Credits',
                buttonText: 'Add Credits',
                payload: {
                  label: 'Clicked on Add Credits',
                  isQwaDashboard: true,
                  onCreditBox: true,
                },
              });
            }}
          >
            - Add Credits
          </p>
        </div>
        <div className="credits-management">
          {decodeJwt(dayPassAuth) && dayPassCreditsRemaining ? (
            <p className="reset-time m-0">Day Pass expires in {timeLeftForDayPass}</p>
          ) : showFreeCredits ? (
            <p className="reset-time m-0">Free credits reset in 8 hours</p>
          ) : (
            <p className="reset-time m-0">Credits reset in {timeLeft}</p>
          )}
          <p
            className="manage-account m-0"
            onClick={() => {
              router.push('/app/manage-account');
              trackAnalyticsEvent({
                eventFor: 'button',
                eventName: 'Clicked on Manage Account',
                buttonText: 'Manage Account',
                payload: {
                  label: 'Clicked on Manage Account',
                  isQwaDashboard: true,
                  onCreditBox: true,
                },
              });
            }}
          >
            Manage Account
          </p>
        </div>
        <button className="individual-btn">
          {' '}
          {planId && !['subscription_cancelled'].includes(session?.session?.user?.qwa_tools_subscription_status)
            ? planId
            : `Free Plan User`}
        </button>
      </div>
    </div>
  );
};

export default CreditsComponent;
